/**
 * Token Logic
 * ฟังก์ชันจัดการ JWT Token
 *
 * Pure utility functions for JWT token handling.
 * ฟังก์ชันยูทิลิตี้สำหรับการจัดการ JWT token
 */

import type { JwtPayload } from './types'

/**
 * Decode a JWT token (base64 payload extraction only).
 * ถอดรหัส JWT token (แยกส่วน payload จาก base64 เท่านั้น)
 *
 * NOTE: This does NOT verify the signature. Signature verification
 * should be done server-side.
 */
export const decodeToken = (token: string): JwtPayload | null => {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null

    const payload = parts[1]
    if (!payload) return null

    // Handle base64url encoding
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )

    return JSON.parse(jsonPayload) as JwtPayload
  } catch {
    return null
  }
}

/**
 * Get the expiry timestamp (in seconds) from a JWT token.
 * ดึง timestamp หมดอายุ (หน่วยวินาที) จาก JWT token
 *
 * @returns expiry timestamp in seconds, or 0 if invalid
 */
export const getTokenExpiry = (token: string): number => {
  const payload = decodeToken(token)
  return payload?.exp ?? 0
}

/**
 * Check if a JWT token is expired.
 * ตรวจสอบว่า JWT token หมดอายุแล้วหรือไม่
 *
 * @param token - JWT token string
 * @param bufferSeconds - seconds before actual expiry to consider expired (default: 30)
 * @returns true if token is expired or will expire within buffer period
 */
export const isTokenExpired = (token: string, bufferSeconds = 30): boolean => {
  const expiry = getTokenExpiry(token)
  if (expiry === 0) return true

  const nowSeconds = Math.floor(Date.now() / 1000)
  return nowSeconds >= expiry - bufferSeconds
}

/**
 * Get remaining lifetime of a token in seconds.
 * ดึงอายุที่เหลือของ token เป็นวินาที
 *
 * @returns remaining seconds, or 0 if expired/invalid
 */
export const getTokenRemainingSeconds = (token: string): number => {
  const expiry = getTokenExpiry(token)
  if (expiry === 0) return 0

  const nowSeconds = Math.floor(Date.now() / 1000)
  const remaining = expiry - nowSeconds
  return remaining > 0 ? remaining : 0
}
